import { Box, Chip, useTheme } from '@mui/material';
import { type ReactElement, useCallback, useState } from 'react';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import { getEntityCardIconSx } from 'src/components/EntitySidePanel/entity-side-panel-layout';
import { SidePanelCard } from 'src/components/SidePanelCard/side-panel-card';
import { SiteTitleTooltip } from 'src/components/SiteComponents/site-title-tooltip';
import { Tooltip } from 'src/components/Tooltip/Tooltip';
import type { Conversation } from 'src/graphql';
import { ConversationSites } from './conversation-sites';
import { useConversationAddress } from './use-conversation-address';

const GENERIC_TITLE = 'Site';

interface ConversationSiteCardProps {
  conversation: Conversation;
}

export const ConversationSiteCard = ({
  conversation,
}: ConversationSiteCardProps): ReactElement => {
  const theme = useTheme();
  const address = useConversationAddress(conversation);
  // the body owns the query that yields this, so the chip appears with the first
  // expand and the collapsed card still costs nothing
  const [availableSitesCount, setAvailableSitesCount] = useState(0);

  const additionalSitesCount = Math.max(availableSitesCount - 1, 0);

  const onAvailableSitesCountChange = useCallback((count: number) => {
    setAvailableSitesCount(count);
  }, []);

  return (
    <SidePanelCard
      data-testid="conversation-site-card"
      // open in both states: with no site the body *is* the invitation to add one,
      // and a card that hides it defeats the reason the card stays in the stack
      defaultOpen
      mountContentOnOpen
      leading={
        <Tooltip title={address?.brand?.name} hideWhenRefIsHidden>
          <DynamicIcon
            data-testid="site-segment-icon"
            name={
              address?.brand?.name.toLowerCase().includes('commercial') ? 'building-2' : 'home'
            }
            library="lucide"
            sx={getEntityCardIconSx(
              address
                ? theme.palette.custom.OV.entity.site
                : theme.palette.custom.OV.sys.onSurfaceVariant,
            )}
          />
        </Tooltip>
      }
      title={
        address?.fullAddress ?? (
          <Box
            component="span"
            sx={(cardTheme) => ({ color: cardTheme.palette.custom.OV.sys.onSurfaceVariant })}
          >
            {GENERIC_TITLE}
          </Box>
        )
      }
      titleTooltip={
        address
          ? (titleNode) => <SiteTitleTooltip address={address}>{titleNode}</SiteTitleTooltip>
          : undefined
      }
      trailing={
        additionalSitesCount ? (
          <Chip
            size="small"
            variant="outlined"
            label={`+${additionalSitesCount} more`}
            sx={{
              borderRadius: 999,
              borderColor: 'divider',
              color: 'text.secondary',
              fontSize: '0.6875rem',
              fontWeight: 500,
              height: 20,
            }}
          />
        ) : null
      }
    >
      <ConversationSites
        conversation={conversation}
        onAvailableSitesCountChange={onAvailableSitesCountChange}
      />
    </SidePanelCard>
  );
};
